import styles from './comment-editor.module.css'

import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { MAX_PLAIN_TEXT_CHARS } from '@disco/constants/discussions'
import type {
  EditorContent,
  RteHandle,
} from '@disco/shared-components/rich-text-editor'
import {
  RichTextEditor,
  trimContent,
} from '@disco/shared-components/rich-text-editor'
import {
  useCreateBulkDiscussionsMutation,
  useCreateDiscussionMutation,
  useUpdateDiscussionMutation,
} from '@disco/state-management/api/discussions'
import { useMentionUsers } from '@disco/state-management/hooks/useMentionUsers'
import { getTrackPlaybackStatus } from '@disco/state-management/selectors/media'
import {
  DISCUSSION_EVENTS,
  getErrorStatus,
} from '@disco/utils/discussion-mixpanel'
import { pushUINotification } from '@disco/utils/notifications'

import type { Discussion, DiscussionVisibility } from 'types/discussion'
import type { TrackId } from 'types/track'

import {
  getDiscussionContentProps,
  sendDiscussionEvent,
} from 'utils/discussion-mixpanel'

import { useSelector, useStore } from 'hooks/use-redux'

import type { DraftContent } from './comment-draft-cache'
import { clearDraft, getDraft, setDraft } from './comment-draft-cache'
import { getTrackNoteToolbarItems } from './toolbar-items'

type SingleTrackCommonProps = {
  trackDurationSeconds?: number
  trackId: TrackId
}

type CreateProps = SingleTrackCommonProps & {
  mode: 'create'
  visibility: DiscussionVisibility
}

type EditProps = SingleTrackCommonProps & {
  mode: 'edit'
  discussion: Discussion
  onCancel: () => void
  onSuccess?: () => void
}

type ReplyProps = SingleTrackCommonProps & {
  mode: 'reply'
  parent: Discussion
  onCancel: () => void
  onSuccess?: () => void
}

type BulkCreateProps = {
  mode: 'bulk-create'
  trackIds: TrackId[]
  visibility: DiscussionVisibility
}

type Props = CreateProps | EditProps | ReplyProps | BulkCreateProps

const draftKeyFor = (props: Props): string => {
  switch (props.mode) {
    case 'create':
      return `new:${props.trackId}:${props.visibility}`
    case 'edit':
      return `edit:${props.discussion.id}`
    case 'reply':
      return `reply:${props.parent.id}`
    case 'bulk-create':
      return `bulk:${[...props.trackIds].sort((a, b) => a - b).join(',')}:${
        props.visibility
      }`
  }
}

const submitTextFor = (props: Props): string => {
  switch (props.mode) {
    case 'create':
    case 'bulk-create':
      return props.visibility === 'internal'
        ? 'Add internal note'
        : 'Add client note'
    case 'edit':
      return 'Update note'
    case 'reply':
      return 'Reply'
  }
}

const initialContentFor = (props: Props): DraftContent | null | undefined => {
  if (props.mode === 'edit') {
    return (props.discussion.body as DraftContent | null | undefined) ?? null
  }
  return null
}

const errorMessageFor = (mode: Props['mode']): string => {
  switch (mode) {
    case 'create':
      return 'Failed to create note'
    case 'edit':
      return 'Failed to update note'
    case 'reply':
      return 'Failed to post reply'
    case 'bulk-create':
      return 'Failed to create notes'
  }
}

const CommentEditor = (props: Props): React.ReactElement => {
  const { mode } = props
  const trackId = mode === 'bulk-create' ? undefined : props.trackId
  const trackDurationSeconds =
    mode === 'bulk-create' ? undefined : props.trackDurationSeconds
  const discussion = mode === 'edit' ? props.discussion : undefined
  const parent = mode === 'reply' ? props.parent : undefined
  const createVisibility =
    mode === 'create' || mode === 'bulk-create' ? props.visibility : undefined
  const bulkTrackIds = mode === 'bulk-create' ? props.trackIds : undefined
  const onCancel =
    mode === 'edit' || mode === 'reply' ? props.onCancel : undefined
  const onSuccess =
    mode === 'edit' || mode === 'reply' ? props.onSuccess : undefined

  const editorRef = React.useRef<RteHandle>(null)
  const navigate = useNavigate()
  const {
    mentionsEnabled: notesV2MentionsEnabled,
    users: mentionUsers,
    usersLoading: mentionUsersLoading,
  } = useMentionUsers()

  const noteVisibility =
    createVisibility ?? parent?.visibility ?? discussion?.visibility
  const mentionsEnabled = notesV2MentionsEnabled && noteVisibility !== 'client'

  const eventTarget = discussion ?? parent
  const eventTargetType = eventTarget?.target_type ?? 'track'
  const eventTargetId = eventTarget?.target_object_id ?? trackId
  const eventVisibility = noteVisibility ?? 'internal'

  const handleInvite = React.useCallback(
    (query: string) => {
      navigate('/settings/users/invites', {
        state: { prefillName: query.trim() },
      })
    },
    [navigate],
  )
  const [createDiscussion] = useCreateDiscussionMutation()
  const [updateDiscussion] = useUpdateDiscussionMutation()
  const [createBulkDiscussions] = useCreateBulkDiscussionsMutation()

  const draftKey = draftKeyFor(props)

  React.useEffect(() => {
    if (mode === 'create' || mode === 'bulk-create') {
      return
    }
    editorRef.current?.getEditor()?.commands.focus('end')
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.scrollIntoView({ block: 'nearest' })
    }
  }, [mode])

  const initialContentRef = React.useRef<DraftContent | null | undefined>(
    undefined,
  )

  if (initialContentRef.current === undefined) {
    initialContentRef.current =
      getDraft(draftKey) ?? initialContentFor(props) ?? null
  }
  const initialContent = initialContentRef.current

  const handleChange = React.useCallback(
    (content: EditorContent) => {
      if (!content.plainText.trim()) {
        clearDraft(draftKey)
      } else {
        setDraft(draftKey, content.json)
      }
    },
    [draftKey],
  )

  const isThisTrackActive = useSelector(state =>
    typeof trackId === 'number'
      ? state.media.trackOriginalId === trackId
      : false,
  )

  const store = useStore()
  const getCurrentTimestamp = React.useCallback(() => {
    if (typeof trackId !== 'number') return undefined
    const state = store.getState()
    if (state.media.trackOriginalId !== trackId) return undefined
    return (
      getTrackPlaybackStatus(state, { trackId })?.playbackPosition ?? undefined
    )
  }, [store, trackId])

  const handleSubmit = React.useCallback(
    async (content: EditorContent) => {
      const trimmedJson = trimContent(content.json)
      if (!trimmedJson.content || trimmedJson.content.length === 0) {
        return
      }
      const trimmedPlainText = content.plainText.trim()
      // Code-point count to match Django len() on the BE
      if ([...trimmedPlainText].length > MAX_PLAIN_TEXT_CHARS) {
        return
      }
      const eventContext = {
        target_type: eventTargetType,
        target_object_id: eventTargetId,
        visibility: eventVisibility,
      }
      try {
        if (mode === 'edit' && discussion) {
          const { id, target_type, target_object_id, visibility, body } =
            discussion

          const unchanged = JSON.stringify(trimmedJson) === JSON.stringify(body)
          if (unchanged) {
            clearDraft(draftKey)
            editorRef.current?.clear()
            onSuccess?.()
            return
          }

          await updateDiscussion({
            id,
            target_type,
            target_object_id,
            data: {
              body_input: trimmedJson,
              body_raw_input: trimmedPlainText,
              visibility,
            },
          }).unwrap()
          sendDiscussionEvent(DISCUSSION_EVENTS.edited, eventContext, {
            discussion_id: id,
            ...getDiscussionContentProps(target_type, trimmedJson),
          })
        } else if (mode === 'reply' && parent) {
          const reply = await createDiscussion({
            target_type: 'track',
            target_object_id: trackId!,
            body_input: trimmedJson,
            body_raw_input: trimmedPlainText,
            visibility: parent.visibility,
            parent: parent.id,
          }).unwrap()
          sendDiscussionEvent(DISCUSSION_EVENTS.replied, eventContext, {
            discussion_id: reply.id,
            parent_id: parent.id,
            ...getDiscussionContentProps(eventTargetType, trimmedJson),
          })
        } else if (mode === 'create' && createVisibility) {
          const created = await createDiscussion({
            target_type: 'track',
            target_object_id: trackId!,
            body_input: trimmedJson,
            body_raw_input: trimmedPlainText,
            visibility: createVisibility,
          }).unwrap()
          sendDiscussionEvent(DISCUSSION_EVENTS.created, eventContext, {
            discussion_id: created.id,
            char_count: [...trimmedPlainText].length,
            ...getDiscussionContentProps(eventTargetType, trimmedJson, {
              withRanges: true,
            }),
          })
        } else if (mode === 'bulk-create' && bulkTrackIds && createVisibility) {
          await createBulkDiscussions({
            body_input: trimmedJson,
            body_raw_input: trimmedPlainText,
            visibility: createVisibility,
            targets: bulkTrackIds.map(id => ({
              target_type: 'track',
              target_object_id: id,
            })),
          }).unwrap()
          sendDiscussionEvent(DISCUSSION_EVENTS.created, eventContext, {
            bulk: true,
            track_count: bulkTrackIds.length,
            char_count: [...trimmedPlainText].length,
            ...getDiscussionContentProps(eventTargetType, trimmedJson, {
              withRanges: true,
            }),
          })
          pushUINotification({
            message: 'Notes created successfully',
            level: 'success',
          })
        }
        clearDraft(draftKey)
        editorRef.current?.clear()
        if (mode === 'edit' || mode === 'reply') {
          onSuccess?.()
        }
      } catch (error) {
        sendDiscussionEvent(DISCUSSION_EVENTS.actionFailed, eventContext, {
          action: mode === 'bulk-create' ? 'create' : mode,
          error_status: getErrorStatus(error),
        })
        pushUINotification({
          message: errorMessageFor(mode),
          level: 'error',
        })
      }
    },
    [
      createDiscussion,
      updateDiscussion,
      createBulkDiscussions,
      draftKey,
      trackId,
      mode,
      discussion,
      parent,
      createVisibility,
      bulkTrackIds,
      onSuccess,
      eventTargetType,
      eventTargetId,
      eventVisibility,
    ],
  )

  const handleCancel = React.useCallback(() => {
    clearDraft(draftKey)
    onCancel?.()
  }, [draftKey, onCancel])

  const showCancel = mode === 'edit' || mode === 'reply'

  return (
    <RichTextEditor
      ref={editorRef}
      type="track"
      customToolbar={getTrackNoteToolbarItems(mentionsEnabled)}
      users={mentionUsers}
      usersLoading={mentionUsersLoading}
      onInvite={mentionsEnabled ? handleInvite : undefined}
      trackDurationSeconds={trackDurationSeconds}
      getCurrentTimestamp={getCurrentTimestamp}
      isTimestampActive={isThisTrackActive}
      submitText={submitTextFor(props)}
      placeholder={
        mentionsEnabled
          ? 'Add a note, timecode or @mention'
          : 'Add a note or timestamp'
      }
      initialContent={initialContent}
      maxLength={MAX_PLAIN_TEXT_CHARS}
      onChange={handleChange}
      onSubmit={handleSubmit}
      onCancel={showCancel ? handleCancel : undefined}
      className={styles.editor}
    />
  )
}

export default CommentEditor
